import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleCopyUrl,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getLetterheadLogoDataURL,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import { Input, Select, TextArea } from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getAddresses } from "../services/soar";
import { getSchema } from "./joi/validation";

function NVBHCNAdultNotice() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const officeInfo = useMemo(() => {
    if (!data.SelectedProgram?.["@value"] || !clientData?.programs) {
      return;
    }

    const selectedProgram = clientData.programs.filter(
      (program) =>
        program.ccx_client_programid === data.SelectedProgram["@value"]
    )[0];

    if (!selectedProgram) {
      return;
    }

    let street2 = <></>;

    if (selectedProgram["fac.street2"]) {
      street2 = (
        <>
          <br />
          {selectedProgram["fac.street2"]}
        </>
      );
    }

    const info = (
      <>
        {selectedProgram.ccx_company["@name"]}
        <br />
        {selectedProgram["fac.street1"]}
        {street2}
        <br />
        {`${selectedProgram["fac.city"]} ${selectedProgram["fac.state"]}, ${selectedProgram["fac.postalcode"]}`}
      </>
    );

    return info;
  }, [clientData, data.SelectedProgram]);

  const typeOptions = [
    {
      _id: "No Show, next appointment is scheduled",
      name: "No Show, next appointment is scheduled",
    },
    {
      _id: "No Show, no more appointments scheduled",
      name: "No Show, no more appointments scheduled",
    },
    {
      _id: "Frequent Cancellations, next appointment scheduled",
      name: "Frequent Cancellations, next appointment scheduled",
    },
    {
      _id: "Frequent Cancellations, no more appointments scheduled",
      name: "Frequent Cancellations, no more appointments scheduled",
    },
    {
      _id: "Hasn't been seen in 30+ days, next appointment scheduled",
      name: "Hasn't been seen in 30+ days, next appointment scheduled",
    },
    {
      _id: "Hasn't been seen in 30+ days, no appointment scheduled",
      name: "Hasn't been seen in 30+ days, no appointment scheduled",
    },
    { _id: "General Letter", name: "General Letter" },
  ];

  useEffect(() => {
    if (submissionid || !data.SelectedProgram || loading || loaded) {
      return;
    }

    const loadAddress = async () => {
      setLoading(true);
      const response = await getAddresses(clientid);

      if (!isMountedRef.current) {
        return;
      }

      const address = response.filter(
        (addy) =>
          addy.ccx_expirationdate === undefined ||
          moment(getFieldDate(addy.ccx_expirationdate)).isAfter(moment())
      )[0];

      const obj = { ...data };

      if (address !== undefined) {
        obj.Address["@value"] = `${address.ccx_line1}${
          address.ccx_line2 !== undefined
            ? `
${address.ccx_line2}`
            : ""
        }
${address.ccx_city}, ${address.ccx_state} ${address.ccx_postalcode}`;
      }
      setData(obj);
      setLoaded(true);
      setLoading(false);
    };

    loadAddress();
  }, [data, clientid, loaded, loading, submissionid]);

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
          letterhead={
            <div className="clearfix mb-2">
              <div className="float-start">
                <img
                  src={getLetterheadLogoDataURL()}
                  alt="safy logo"
                  className="me-4"
                  style={{ width: "200px", height: "auto" }}
                ></img>
              </div>
              <div className="float-end">{officeInfo}</div>
            </div>
          }
          hideHeaderOnPrint={true}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          {formsyDetails.ClientProgramName}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="clearfix d-print-none">
                <div className="float-end">
                  <button
                    className="btn btn-link"
                    title="Copy and Open Encounter"
                    onClick={() => {
                      const encounterEnvironment =
                        process.env.REACT_APP_ENVIRONMENT === "Production"
                          ? "ccplive-cp"
                          : "d365base-cp";
                      handleCopyUrl(location.pathname).then(() => {
                        window.open(
                          `https://${encounterEnvironment}.safy.org/dist/EncounterPortal/index.html?contactid=%7B${clientid}%7D`
                        );
                      });
                    }}
                  >
                    Copy form link and launch to Encounter
                  </button>
                </div>
              </div>
              <div className="d-print-none">
                <div className="row row-cols-1 row-cols-sm-1 row-cols-md-3 row-cols-lg-3 row-cols-xl-3">
                  <div className="col">
                    <Input
                      name="Date"
                      label="Date of Letter:"
                      error={errors.Date}
                      value={data.Date["@value"]}
                      onChange={handleChange}
                      identical={compare("Date")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      type="date"
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="Client"
                      label="Client Name:"
                      error={errors.Client}
                      value={data.Client["@value"]}
                      onChange={handleChange}
                      identical={compare("Client")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="Clinician"
                      label="Clinician Name and Credentials:"
                      error={errors.Clinician}
                      value={data.Clinician["@value"]}
                      onChange={handleChange}
                      identical={compare("Clinician")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Select
                      name="Type"
                      label="Type of Letter:"
                      error={errors.Type}
                      value={data.Type["@value"]}
                      options={typeOptions}
                      onChange={handleChange}
                      identical={compare("Type")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  {data.Type["@value"] !== "General Letter" ? (
                    <>
                      <div className="col">
                        <Input
                          name="LastDate"
                          label="Date of/since missed/cancelled/last appointment/services:"
                          error={errors.LastDate}
                          value={data.LastDate["@value"]}
                          onChange={handleChange}
                          identical={compare("LastDate")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                          type={
                            data.Type["@value"] !==
                              "Frequent Cancellations, next appointment scheduled" &&
                            data.Type["@value"] !==
                              "Frequent Cancellations, no more appointments scheduled"
                              ? "date"
                              : null
                          }
                        />
                      </div>
                      {data.Type["@value"] ===
                        "No Show, next appointment is scheduled" ||
                      data.Type["@value"] ===
                        "Frequent Cancellations, next appointment scheduled" ||
                      data.Type["@value"] ===
                        "Hasn't been seen in 30+ days, next appointment scheduled" ? (
                        <div className="col">
                          <Input
                            name="NextDate"
                            label="Date of Next Appointment:"
                            error={errors.NextDate}
                            value={data.NextDate["@value"]}
                            onChange={handleChange}
                            identical={compare("NextDate")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            type="datetime-local"
                          />
                        </div>
                      ) : (
                        <div className="col"></div>
                      )}
                    </>
                  ) : null}
                  <div className="col">
                    <Input
                      name="Address"
                      label="Client Address:"
                      error={errors.Address}
                      value={data.Address["@value"]}
                      onChange={handleChange}
                      identical={compare("Address")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col"></div>
                </div>
              </div>
              <p>
                {data.Date["@value"] !== undefined ? (
                  moment(data.Date["@value"]).format("LL")
                ) : (
                  <i>(Date of Letter)</i>
                )}
              </p>
              <p style={{ whiteSpace: "pre-line" }}>
                {data.Address["@value"] !== undefined ? (
                  `${data.Address["@value"]}`
                ) : (
                  <i>(Client Address)</i>
                )}
              </p>
              <p>
                Dear{" "}
                {data.Client["@value"] !== undefined ? (
                  data.Client["@value"]
                ) : (
                  <i>(Client Name)</i>
                )}
                ,
              </p>
              {data.Type["@value"] ===
              "No Show, next appointment is scheduled" ? (
                <p style={{ textIndent: "40px" }}>
                  We miss you! You did not attend the last scheduled appointment
                  on{" "}
                  {data.LastDate["@value"] !== undefined &&
                  data.LastDate["@value"] !== "" ? (
                    moment(data.LastDate["@value"]).format("LL")
                  ) : (
                    <i>(Date of Session)</i>
                  )}
                  . If you would like to continue services at SAFY, please make
                  sure you attend the next scheduled appointment on{" "}
                  {data.NextDate["@value"] !== undefined ? (
                    moment(data.NextDate["@value"]).format("dddd, LL [at] LT")
                  ) : (
                    <i>(Date &amp; Time of Next Appointment)</i>
                  )}
                  .
                </p>
              ) : null}
              {data.Type["@value"] ===
              "No Show, no more appointments scheduled" ? (
                <p style={{ textIndent: "40px" }}>
                  We miss you! You did not attend the last scheduled appointment
                  on{" "}
                  {data.LastDate["@value"] !== undefined &&
                  data.LastDate["@value"] !== "" ? (
                    moment(data.LastDate["@value"]).format("LL")
                  ) : (
                    <i>(Date of Session)</i>
                  )}
                  . You have no more scheduled appointments at this time. If you
                  would like to continue services at SAFY, please call our
                  office to schedule your next appointment.
                </p>
              ) : null}
              {data.Type["@value"] ===
              "Frequent Cancellations, next appointment scheduled" ? (
                <p style={{ textIndent: "40px" }}>
                  Due to the frequency of missed appointments, your services are
                  at risk of being closed. We show missed appointments on{" "}
                  {data.LastDate["@value"] !== undefined &&
                  data.LastDate["@value"] !== "" ? (
                    data.LastDate["@value"]
                  ) : (
                    <i>(Date of Services)</i>
                  )}
                  . If you would like to continue services at SAFY, please make
                  sure you attend the next scheduled appointment on{" "}
                  {data.NextDate["@value"] !== undefined ? (
                    moment(data.NextDate["@value"]).format("dddd, LL [at] LT")
                  ) : (
                    <i>(Date &amp; Time of Next Appointment)</i>
                  )}
                  .
                </p>
              ) : null}
              {data.Type["@value"] ===
              "Frequent Cancellations, no more appointments scheduled" ? (
                <p style={{ textIndent: "40px" }}>
                  Due to the frequency of missed appointments, your services are
                  at risk of being closed. We show missed appointments on{" "}
                  {data.LastDate["@value"] !== undefined &&
                  data.LastDate["@value"] !== "" ? (
                    data.LastDate["@value"]
                  ) : (
                    <i>(Date of Services)</i>
                  )}
                  , and no more further appointments scheduled. If you would
                  like to continue services at SAFY, please call our office to
                  schedule the next appointment.
                </p>
              ) : null}
              {data.Type["@value"] ===
              "Hasn't been seen in 30+ days, next appointment scheduled" ? (
                <>
                  <p style={{ textIndent: "40px" }}>
                    We miss you! You have not been seen since{" "}
                    {data.LastDate["@value"] !== undefined &&
                    data.LastDate["@value"] !== "" ? (
                      moment(data.LastDate["@value"]).format("LL")
                    ) : (
                      <i>(Date of Last Session)</i>
                    )}
                    . If you would like to continue services at SAFY, you must
                    attend the next scheduled appointment on{" "}
                    {data.NextDate["@value"] !== undefined ? (
                      moment(data.NextDate["@value"]).format("dddd, LL [at] LT")
                    ) : (
                      <i>(Date &amp; Time of Next Appointment)</i>
                    )}
                    .
                    <br />
                  </p>
                  Please note that we will close your case and terminate
                  services if:
                  <ul>
                    <li>
                      You do not attend the next scheduled appointment; or
                    </li>
                    <li>There are any more cancellations or no shows.</li>
                  </ul>
                </>
              ) : null}
              {data.Type["@value"] ===
              "Hasn't been seen in 30+ days, no appointment scheduled" ? (
                <p style={{ textIndent: "40px" }}>
                  We miss you! You have not been seen since{" "}
                  {data.LastDate["@value"] !== undefined &&
                  data.LastDate["@value"] !== "" ? (
                    moment(data.LastDate["@value"]).format("LL")
                  ) : (
                    <i>(Date of Last Session)</i>
                  )}{" "}
                  and have no more scheduled appointments at this time. If you
                  would like to continue services at SAFY, please call our
                  office to schedule the next appointment.
                </p>
              ) : null}
              {data.Type["@value"] === "General Letter" ? (
                <>
                  <TextArea
                    name="General"
                    error={errors.General}
                    value={data.General["@value"]}
                    onChange={handleChange}
                    identical={compare("General")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              ) : null}
              {data.Type["@value"] !== "General Letter" ? (
                <>
                  <p style={{ textIndent: "40px" }}>
                    Regular and consistent attendance is an important factor in
                    your therapy. Your active participation in therapy sessions
                    helps us to achieve the set therapy goals. It is our desire
                    to serve our clients in the best possible manner. If SAFY
                    Services are not fitting your needs, please contact our
                    Clinical Treatment Director, Melinda Rhoades, LCSW, to
                    discuss alternative treatment options including:
                    <br />
                    <span style={{ textIndent: "0px" }}>
                      <ul>
                        <li>
                          Referral for services at another community agency.
                        </li>
                        <li>
                          Closing services and checking back in at a later date.
                        </li>
                      </ul>
                    </span>
                  </p>
                  {data.Type["@value"] !==
                  "Hasn't been seen in 30+ days, next appointment scheduled" ? (
                    <p>
                      Please note that we will close your case and terminate
                      services if:
                      <ul>
                        <li>
                          There are any more cancellations or no shows, or
                        </li>
                        <li>
                          We do not hear from you within 2 weeks of the date of
                          this letter.
                        </li>
                      </ul>
                    </p>
                  ) : null}
                  <p>
                    If your case is closed at SAFY, there is always the
                    opportunity to re-open services in the future.
                  </p>
                  <p>
                    Please contact us:
                    <ul>
                      <li>To schedule your next service at SAFY; or </li>
                      <li>
                        If you'd like to close your services with SAFY; or
                      </li>
                      <li>
                        If you need to discuss extraordinary circumstances
                        impacting engagement and attendance of services; or
                      </li>
                      <li>If you were dissatisfied with your services; or</li>
                      <li>If you have any further questions or concerns.</li>
                    </ul>
                  </p>
                  <TextArea
                    name="ContactInfo"
                    label="As a reminder, SAFY Behavioral Health can be contacted by:"
                    error={errors.ContactInfo}
                    value={data.ContactInfo["@value"]}
                    onChange={handleChange}
                    identical={compare("ContactInfo")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    rows="3"
                  />
                </>
              ) : null}
              Sincerely,
              <p>{data.Clinician["@value"]}</p>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <span className="d-print-none">
              <Signature
                canvasWidth="450"
                canvasHeight="200"
                modalTitle="Report Signatures"
                careTeams={careTeams}
                signatures={signatures}
                onSign={saveSignature}
                clientData={clientData.client}
                showModal={showSignatureModal}
                onHide={() => {
                  setShowSignatureModal(false);
                }}
                finalSignType={
                  formsyConfig.HelperConfiguration &&
                  JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
                }
                initialSignatureType={
                  formsyConfig.HelperConfiguration &&
                  JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
                }
              ></Signature>
            </span>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default NVBHCNAdultNotice;
